import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePrograms } from '../context/ProgramsContext';
import api from '../utils/api';
import { SelectDrop } from '../components/ui';
import './HostFinderPage.css';
import canadianProvinces from '../data/canadianProvinces.json';
import { buildProspectPayload } from '../utils/hostFinderPayload';
import SavedHostProspects from '../components/SavedHostProspects';

const AMBER = '#F5A623';

function DI({ d, size = 18, stroke = 1.75 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 }}>
      {typeof d === 'string' ? <path d={d} /> : d}
    </svg>
  );
}

// Programs the finder has search tags for
const FINDER_PROGRAMS = ['GOSC', 'MOA', 'AT', 'BMT', 'AMT'];

export default function HostFinderPage() {
  const { user } = useAuth();
  const { programs } = usePrograms();
  const [program, setProgram] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [data, setData] = useState(null);           // { program, city, count, results }
  const [added, setAdded] = useState({});           // osm id -> 'saving' | 'done'
  const [saved, setSaved] = useState(null);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState('');

  const firstName = user?.full_name?.split(' ')[0] || 'there';

  const programOpts = [
    { value: '', label: 'Select a program…' },
    ...(programs || []).filter(p => FINDER_PROGRAMS.includes(p.code)).map(p => ({ value: p.code, label: p.name })),
  ];

  const search = async () => {
    if (loading) return;
    if (!program || !city.trim() || !province) { setErrorCode(''); setError('Pick a program, enter a city, and select a Canadian province or territory.'); return; }
    setError(''); setErrorCode(''); setLoading(true); setData(null); setAdded({});
    setSaved(null); setSavedError(''); setSavedLoading(true);
    const savedRequest = api.get('/host-finder/saved', {
      params: { city: city.trim(), province }, timeout: 15000,
    }).then(res => setSaved(res.data)).catch(() => {
      setSavedError('Saved prospects could not be loaded. This is separate from the directory search.');
    }).finally(() => setSavedLoading(false));
    try {
      const res = await api.post('/host-finder/search', { program, city: city.trim(), province }, { timeout: 25000 });
      setData(res.data);
    } catch (e) {
      setErrorCode(e.response?.data?.code || (e.code === 'ECONNABORTED' ? 'timeout' : 'network'));
      setError(e.response?.data?.error || 'The search could not reach the directory in time. Please retry; this is not an empty search result.');
    } finally {
      await savedRequest;
      setLoading(false);
    }
  };

  const addAsHost = async (r) => {
    setAdded(a => ({ ...a, [r.osm]: 'saving' }));
    try {
      await api.post('/hosts/prospects', buildProspectPayload(r, data));
      setAdded(a => ({ ...a, [r.osm]: 'done' }));
    } catch {
      setAdded(a => { const n = { ...a }; delete n[r.osm]; return n; });
      setErrorCode('');
      setError(`Could not add ${r.name}.`);
    }
  };

  const withContact = data?.results.filter(r => r.phone || r.website || r.email).length || 0;

  return (
    <>
      {/* Hero */}
      <div className="h-hero">
        <div className="h-hero-decor" aria-hidden="true">
          <svg viewBox="0 0 800 320" preserveAspectRatio="xMaxYMid slice" width="100%" height="100%">
            <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
              <circle cx="720" cy="160" r="80" /><circle cx="720" cy="160" r="140" />
              <circle cx="720" cy="160" r="200" /><circle cx="720" cy="160" r="260" />
            </g>
          </svg>
        </div>
        <div className="h-hero-inner">
          <div className="h-hero-crumb"><span>Host Specialist</span><span className="h-crumb-sep">·</span><span>Host Finder</span></div>
          <h1 className="h-hero-title">Find new <span className="h-hero-accent">hosts</span>.</h1>
          <p className="h-hero-lede">
            {firstName}, pick a program, city, and province or territory — searches are restricted to Canada.
          </p>
        </div>
      </div>

      <div className="h-content">
        <div className="h-content-inner">
          {/* Search bar */}
          <div className="hf-searchbar">
            <SelectDrop value={program} onChange={setProgram} options={programOpts} minWidth={220} />
            <div className="hf-city">
              <DI d={<g><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></g>} size={16} />
              <input
                className="hf-city-input"
                placeholder="City (e.g. Calgary)"
                 aria-label="City"
                value={city}
                onChange={e => setCity(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && search()}
              />
            </div>
            <label className="hf-province">
              <span>Province / territory</span>
              <select value={province} onChange={e => setProvince(e.target.value)}>
                <option value="">Select province / territory…</option>
                {Object.entries(canadianProvinces).map(([code, name]) => (
                  <option key={code} value={code}>{name} ({code})</option>
                ))}
              </select>
            </label>
            <button className="hf-search-btn" onClick={search} disabled={loading}>
              {loading ? 'Searching…' : <>Search <DI d="M5 12h14M13 6l6 6-6 6" size={15} /></>}
            </button>
          </div>

          <SavedHostProspects data={saved} loading={savedLoading} error={savedError} />

          {error && <div className="hf-error" role="alert">External directory: {error}{errorCode && <> (Reference: {errorCode})</>}{saved?.count > 0 && <> Your saved prospects are still available above.</>}</div>}
          {data?.count === 0 && <div className="hf-empty" role="status">No businesses found in this search — try a larger nearby city or a different program.</div>}

          {loading && (
            <div className="hf-empty">Scanning business listings in {city}, {province}, Canada… this can take a few seconds.</div>
          )}

          {data && data.count > 0 && (
            <>
              <div className="hf-resulthead">
                <div>
                  <strong>{data.count}</strong> businesses found in {data.city}, {data.province}, Canada
                  <span className="hf-sub"> · {withContact} with contact info</span>
                </div>
                <div className="hf-sub">
                  {data.provider === 'geoapify' && <><a href="https://www.geoapify.com/" target="_blank" rel="noreferrer">Powered by Geoapify</a> · </>}
                  © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>
                  {' · '}Potential prospects; availability not confirmed
                  {data.cached && data.searchedAt && <> · Cached {new Date(data.searchedAt).toLocaleTimeString()}</>}
                  {data.limited && <> · Showing a limited selection, not every business in the city</>}
                </div>
              </div>

              <div className="hf-grid">
                {data.results.map((r) => (
                  <div key={r.osm} className="hf-card">
                    <div className="hf-card-top">
                      <div className="hf-card-name">{r.name}</div>
                      <span className="hf-tag">{r.category}</span>
                    </div>
                    {r.address && <div className="hf-card-addr"><DI d={<g><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></g>} size={13} /> {r.address}</div>}
                    <div className="hf-card-contacts">
                      {r.phone && <a href={`tel:${r.phone}`} className="hf-contact"><DI d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" size={13} /> {r.phone}</a>}
                      {r.email && <a href={`mailto:${r.email}`} className="hf-contact"><DI d={<g><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></g>} size={13} /> {r.email}</a>}
                      {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="hf-contact"><DI d={<g><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" /></g>} size={13} /> Website</a>}
                      {!r.phone && !r.email && !r.website && <span className="hf-nocontact">No contact info listed</span>}
                    </div>
                    <div className="hf-card-foot">
                      {added[r.osm] === 'done'
                        ? <span className="hf-added"><DI d="M20 6L9 17l-5-5" size={13} /> Added to prospects</span>
                        : <button className="hf-add-btn" disabled={added[r.osm] === 'saving'} onClick={() => addAsHost(r)}>
                            {added[r.osm] === 'saving' ? 'Adding…' : <><DI d="M12 5v14M5 12h14" size={13} /> Add as prospect</>}
                          </button>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!loading && !data && !error && (
            <div className="hf-empty">Pick a program, city, and Canadian province or territory above to find potential host organizations.</div>
          )}
        </div>
      </div>
    </>
  );
}
